import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  MAX_AMOUNT_MINOR,
  type ConnectionDetail,
  type Me,
  type TransactionCategory,
  type TransactionWithEvents,
} from '@debtbot/shared';
import {
  useConnection,
  useCreateTransaction,
  useMe,
  useTransaction,
  useUpdateTransaction,
} from '../api/hooks';
import { ApiError } from '../api/errors';
import { AmountInput, digitsToMinor } from '../components/AmountInput';
import { CategoryChips } from '../components/CategoryChips';
import { ErrorBanner } from '../components/ErrorBanner';
import { MainButtonFallback } from '../components/MainButtonFallback';
import { Segmented } from '../components/Segmented';
import { ListSkeleton } from '../components/Skeleton';
import { useToast } from '../components/Toast';
import { useT } from '../i18n';
import { formatMoney, fullName } from '../lib/format';
import { haptic } from '../telegram/haptic';
import { useClosingConfirmation, useMainButton } from '../telegram/ui';

type Payer = 'me' | 'peer';

/** Один экран на создание и редактирование (PROJECT_SPEC §6.5). Загружает данные, затем рендерит форму. */
export function TransactionFormScreen({ mode }: { mode: 'new' | 'edit' }) {
  const t = useT();
  const params = useParams();
  const me = useMe();
  const existing = useTransaction(mode === 'edit' ? (params.id ?? '') : '');
  const connectionId = mode === 'new' ? (params.id ?? '') : (existing.data?.connection_id ?? '');
  const conn = useConnection(connectionId);

  const loading = me.isPending || conn.isPending || (mode === 'edit' && existing.isPending);
  // в режиме new запрос existing отключён и остаётся в isPending — это не загрузка
  const error = me.error ?? conn.error ?? existing.error;

  if (loading || error || !me.data || !conn.data || (mode === 'edit' && !existing.data)) {
    return (
      <main className="min-h-dvh bg-bg text-text px-4 py-4 flex flex-col gap-4">
        <h1 className="text-xl font-semibold">
          {mode === 'new' ? t.form.newTitle : t.form.editTitle}
        </h1>
        {error ? <ErrorBanner error={error} /> : <ListSkeleton rows={2} />}
      </main>
    );
  }

  return (
    <TransactionForm
      key={existing.data?.version ?? 'new'}
      mode={mode}
      me={me.data}
      conn={conn.data}
      existing={mode === 'edit' ? existing.data : undefined}
      onConflict={() => void existing.refetch()}
    />
  );
}

function TransactionForm({
  mode,
  me,
  conn,
  existing,
  onConflict,
}: {
  mode: 'new' | 'edit';
  me: Me;
  conn: ConnectionDetail;
  existing?: TransactionWithEvents;
  onConflict: () => void;
}) {
  const t = useT();
  const navigate = useNavigate();
  const [payer, setPayer] = useState<Payer>(
    existing ? (existing.payer_id === me.id ? 'me' : 'peer') : 'me',
  );
  const [digits, setDigits] = useState(existing ? String(Math.round(existing.amount / 100)) : '');
  const [category, setCategory] = useState<TransactionCategory | null>(existing?.category ?? null);
  const [note, setNote] = useState(existing?.note ?? '');

  // Один ключ идемпотентности на всё время жизни формы — повторный тап не создаст дубль
  const clientRequestId = useMemo(() => crypto.randomUUID(), []);
  const create = useCreateTransaction(conn.id, me.id);
  const update = useUpdateTransaction(existing?.id ?? '', conn.id);
  const toast = useToast();

  const amount = digitsToMinor(digits, 'UZS');
  const valid = amount >= 1 && amount <= MAX_AMOUNT_MINOR;
  const warning =
    amount > MAX_AMOUNT_MINOR
      ? t.form.maxAmount
      : amount > 10_000_000_000
        ? t.form.bigAmount
        : null;
  const dirty = digits !== '' || note !== '' || category !== null;
  useClosingConfirmation(mode === 'new' && dirty);

  const peerName = fullName(conn.peer);
  const buttonText =
    mode === 'edit'
      ? t.form.save
      : amount > 0
        ? t.form.add(formatMoney({ amount, currency: 'UZS' }))
        : t.form.addEmpty;
  const pending = create.isPending || update.isPending;

  const submit = async () => {
    if (!valid || pending) return;
    haptic.tap();
    const payerId = payer === 'me' ? me.id : conn.peer.id;
    try {
      if (mode === 'new') {
        await create.mutateAsync({
          client_request_id: clientRequestId,
          type: 'EXPENSE',
          payer_id: payerId,
          amount,
          currency: 'UZS',
          category,
          note: note.trim() || null,
          expected_balance: null,
        });
        haptic.success();
        toast({ text: t.person.added });
      } else {
        await update.mutateAsync({
          version: existing!.version,
          amount,
          payer_id: payerId,
          category,
          note: note.trim() || null,
        });
        haptic.success();
      }
      void navigate(-1);
    } catch (err) {
      haptic.error();
      if (err instanceof ApiError && err.code === 'VERSION_CONFLICT') {
        toast({ text: t.errors.conflict });
        onConflict();
      }
    }
  };

  useMainButton({
    text: buttonText,
    enabled: valid,
    loading: pending,
    onClick: () => void submit(),
  });

  const mutationError = create.isError ? create.error : update.isError ? update.error : null;

  return (
    <main className="min-h-dvh bg-bg text-text px-4 py-4 pb-24 flex flex-col gap-4">
      <h1 className="text-xl font-semibold">
        {mode === 'new' ? t.form.newTitle : t.form.editTitle}
      </h1>
      {mutationError && <ErrorBanner error={mutationError} />}
      <Segmented<Payer>
        value={payer}
        onChange={setPayer}
        options={[
          { value: 'me', label: t.form.iPaid },
          { value: 'peer', label: t.form.peerPaid(peerName) },
        ]}
      />
      <AmountInput
        digits={digits}
        onChange={setDigits}
        warning={warning}
        autoFocus={mode === 'new'}
      />
      <CategoryChips value={category} onChange={setCategory} />
      <input
        data-testid="note-input"
        value={note}
        onChange={(e) => setNote(e.target.value.slice(0, 100))}
        placeholder={t.form.note}
        className="rounded-xl bg-secondary-bg px-4 py-3 outline-none placeholder:text-hint"
      />
      <MainButtonFallback
        text={buttonText}
        disabled={!valid || pending}
        onClick={() => void submit()}
      />
    </main>
  );
}
